import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  SellerProfileCard,
  SellerProfileCardSkeleton,
  ProductCard,
  RatingAggregate,
  ReviewList,
} from '@/features/mercado/components';
import {
  useSellerProfile,
  useSellerRatings,
  useListings,
} from '@/features/mercado/api';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { MOCK_TENANT_ID } from '@/lib/auth';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';

export function VendedorRoute() {
  const { sellerId } = useParams<{ sellerId: string }>();
  const navigate = useNavigate();
  const { condoId } = useCondoContext();
  const tenantId = condoId || MOCK_TENANT_ID;

  const { data: profile, isLoading: profileLoading } = useSellerProfile(
    sellerId ?? '',
  );
  const { data: ratingsData, isLoading: ratingsLoading } = useSellerRatings(
    sellerId ?? '',
  );
  const { data: listingsData } = useListings();

  const ratings = ratingsData?.items ?? [];
  const sellerListings = (listingsData?.items ?? []).filter(
    (l) => l.sellerId === sellerId && l.status === 'active',
  );

  // Compute rating distribution for the aggregate component
  const distribution = {
    counts: [0, 0, 0, 0, 0] as [number, number, number, number, number],
    total: profile
      ? profile.ratingsPositive + profile.ratingsNeutral + profile.ratingsNegative
      : 0,
    average: 0,
  };
  if (profile) {
    // Map positive/neutral/negative to 5-star scale for display
    distribution.counts[4] = profile.ratingsPositive; // 5 stars
    distribution.counts[2] = profile.ratingsNeutral; // 3 stars
    distribution.counts[0] = profile.ratingsNegative; // 1 star
    const total = distribution.total;
    distribution.average =
      total > 0
        ? (5 * profile.ratingsPositive +
            3 * profile.ratingsNeutral +
            1 * profile.ratingsNegative) /
          total
        : 0;
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Comunidad · Mercado Vecinal"
        title={profile?.displayName ?? 'Vendedor'}
        subtitle="Perfil, publicaciones y calificaciones."
        action={
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[340px_1fr]">
        {/* Left column — profile */}
        <div className="space-y-6">
          {profileLoading ? (
            <SellerProfileCardSkeleton />
          ) : profile ? (
            <SellerProfileCard
              name={profile.displayName}
              rating={distribution.average}
              reviewCount={distribution.total}
              memberSince={profile.memberSince}
              condominiumName={profile.condominiumName}
              isSameBuilding={profile.tenantId === tenantId}
              responseTime="< 1 hora"
              itemsSold={profile.itemsSold}
            />
          ) : null}

          {/* Rating aggregate */}
          {!ratingsLoading && distribution.total > 0 && (
            <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-5">
              <h3 className="eyebrow mb-4">Calificaciones</h3>
              <RatingAggregate distribution={distribution} />
            </div>
          )}
        </div>

        {/* Right column — listings + reviews */}
        <div className="space-y-8">
          {/* Seller's active listings */}
          <section>
            <h2 className="eyebrow mb-4">
              Publicaciones activas ({sellerListings.length})
            </h2>
            {sellerListings.length === 0 ? (
              <p className="text-sm text-[var(--color-ink-muted)]">
                Sin publicaciones activas
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {sellerListings.map((listing) => (
                  <ProductCard
                    key={listing.id}
                    id={listing.id}
                    imageUrl={listing.photos[0]?.url}
                    title={listing.title}
                    priceCLP={listing.priceCLP}
                    sellerName={listing.sellerName}
                    sellerRating={distribution.average}
                    condition={listing.condition}
                    createdAt={listing.createdAt}
                    isSameBuilding={listing.sellerTenantId === tenantId}
                    condominiumName={listing.condominiumName}
                    onClick={() => navigate(`/app/condominios/${tenantId}/mercado/${listing.id}`)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Reviews */}
          <section>
            <h2 className="eyebrow mb-4">Resenas ({ratings.length})</h2>
            <ReviewList
              reviews={ratings.map((r) => ({
                id: r.id,
                authorName: 'Comprador',
                rating: r.rating === 'positive' ? 5 : r.rating === 'neutral' ? 3 : 1,
                text: r.comment,
                createdAt: r.createdAt,
              }))}
            />
          </section>
        </div>
      </div>
    </PageContainer>
  );
}
